import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button, PageContainer } from "@swot/base-components";

import { CmtsSmartDataGrid, type CmtsGridColumn } from "../data-grid/index.js";

import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";
import { complaintApi } from "../../services/complaintApi.js";
import { cmtsComplaintDrilldownFilterFromSearch } from "../../core/complaints/cmtsComplaintDrilldownFilterMapper.js";
import { normalizeComplaintUiError } from "../../core/errors/cmtsComplaintErrorNormalizer.js";

import type { ComplaintSummaryDto } from "../../../types/complaint.types.js";

type ComplaintGridRow = ComplaintSummaryDto & {
  readonly actions?: never | undefined;
};

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type CmtsComplaintRegisterGridProps = {
  readonly records?: readonly ComplaintSummaryDto[] | undefined;
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;

  readonly onSelect?:
    ((record: ComplaintSummaryDto) => void | Promise<void>) | undefined;
  readonly onOpenWorkflow?:
    ((record: ComplaintSummaryDto) => void | Promise<void>) | undefined;
  readonly onOpenEvidence?:
    ((record: ComplaintSummaryDto) => void | Promise<void>) | undefined;
  readonly onExport?:
    | ((payload: {
        readonly rows?: readonly ComplaintSummaryDto[] | undefined;
      }) => void | Promise<void>)
    | undefined;
};

/* -------------------------------------------------------------------------- */
/* File Download Utility Helper                                               */
/* -------------------------------------------------------------------------- */

function downloadDatasetFile(
  dataRows: readonly ComplaintSummaryDto[],
  filename = "complaint-register-export.csv",
  mimeType = "text/csv;charset=utf-8;",
) {
  if (!dataRows || dataRows.length === 0) {
    return;
  }

  // Extract keys dynamically or define explicitly for clean table columns
  const headers = [
    "complaintCode",
    "title",
    "status",
    "severity",
    "slaState",
    "createdAt",
  ];

  // Format CSV rows and escape double quotes
  const csvHeaders = headers.map((h) => `"${h}"`).join(",");
  const csvBody = dataRows
    .map((row) =>
      headers
        .map((field) => {
          const val = row[field as keyof ComplaintSummaryDto] ?? "";
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(","),
    )
    .join("\n");

  const csvContent = `${csvHeaders}\n${csvBody}`;
  const blob = new Blob([csvContent], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* -------------------------------------------------------------------------- */
/* Action Card Component                                                      */
/* -------------------------------------------------------------------------- */

type ActionCardProps = {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly danger?: boolean;
  readonly onClick: () => void;
};

function ActionCard(props: ActionCardProps) {
  return (
    <button
      type="button"
      className={`cmts-action-card ${props.danger ? "cmts-action-card--danger" : ""}`}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <div className="cmts-action-card__icon">{props.icon}</div>
      <div className="cmts-action-card__content">
        <strong className="cmts-action-card__title">{props.title}</strong>
        <span className="cmts-action-card__description">
          {props.description}
        </span>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Badge Component Helpers                                                   */
/* -------------------------------------------------------------------------- */

function StatusBadge({ status }: { status: string }) {
  const normalized = normalizeBadgeCode(status);
  return (
    <span
      className={`cmts-complaint-status cmts-complaint-status--${normalized}`}
    >
      {displayBadgeLabel(status)}
    </span>
  );
}

function SlaBadge({ slaState }: { slaState: string }) {
  const normalized = normalizeBadgeCode(slaState);
  return (
    <span className={`cmts-complaint-sla cmts-complaint-sla--${normalized}`}>
      {displayBadgeLabel(slaState)}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const normalized = normalizeBadgeCode(severity);
  return (
    <span
      className={`cmts-complaint-severity cmts-complaint-severity--${normalized}`}
    >
      {displayBadgeLabel(severity)}
    </span>
  );
}

function normalizeBadgeCode(value: string | null | undefined): string {
  const code = String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return code || "unknown";
}

function displayBadgeLabel(value: string | null | undefined): string {
  return String(value ?? "Unknown")
    .trim()
    .replace(/[_-]+/g, " ");
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export function CmtsComplaintRegisterGrid(
  props: CmtsComplaintRegisterGridProps,
) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchText, setSearchText] = useState(
    searchParams.get("search") ?? "",
  );
  const [backendRows, setBackendRows] = useState<ComplaintSummaryDto[]>([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);

  const [actionsFor, setActionsFor] = useState<ComplaintSummaryDto | null>(
    null,
  );

  const backendListMode = props.records === undefined;
  const searchKey = searchParams.toString();

  /* ------------------------------------------------------------------------ */
  /* Load complaints                                                          */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!backendListMode) return undefined;

    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      setBackendLoading(true);
      setBackendError(null);

      const query = cmtsComplaintDrilldownFilterFromSearch(searchParams);

      void complaintApi
        .list(query, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) {
            setBackendRows([...result.items]);
          }
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            setBackendError(
              normalizeComplaintUiError(
                error,
                "Complaint register could not be loaded.",
              ).userMessage,
            );
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setBackendLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [backendListMode, searchKey, searchParams]);

  /* ------------------------------------------------------------------------ */
  /* Rows & States                                                            */
  /* ------------------------------------------------------------------------ */

  const rows = useMemo(
    () => props.records ?? backendRows,
    [backendRows, props.records],
  );
  const loading = props.loading ?? backendLoading;
  const error = props.error ?? backendError;

  const handleSearch = () => {
    const next = new URLSearchParams(searchParams);
    if (searchText.trim()) {
      next.set("search", searchText.trim());
    } else {
      next.delete("search");
    }
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  /* ------------------------------------------------------------------------ */
  /* Columns                                                                  */
  /* ------------------------------------------------------------------------ */

  const columns = useMemo<readonly CmtsGridColumn<ComplaintGridRow>[]>(
    () => [
      {
        id: "complaintCode",
        header: "Complaint Code",
        minWidth: 160,
        sortable: true,
        render: (row) => (
          <span className="cmts-text-code">{row.complaintCode}</span>
        ),
      },
      {
        id: "title",
        header: "Title",
        minWidth: 260,
        sortable: true,
        render: (row) => (
          <span className="cmts-text-title" title={row.title}>
            {row.title}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        minWidth: 140,
        sortable: true,
        filterable: true,
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: "severity",
        header: "Severity",
        minWidth: 120,
        sortable: true,
        filterable: true,
        render: (row) => <SeverityBadge severity={row.severity} />,
      },
      {
        id: "slaState",
        header: "SLA Status",
        minWidth: 130,
        sortable: true,
        render: (row) => <SlaBadge slaState={row.slaState} />,
      },
      {
        id: "createdAt",
        header: "Created Date",
        minWidth: 170,
        sortable: true,
        render: (row) => {
          if (!row.createdAt) return "-";
          return (
            <span className="cmts-text-muted">
              {new Date(row.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 110,
        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label="Open complaint actions"
            onClick={(event) => {
              event.stopPropagation();
              setActionsFor(row);
            }}
          >
            <span
              className="cmts-notification-actions-button__icon"
              aria-hidden="true"
            >
              ⋮
            </span>
            <span>Actions</span>
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Complaint Register"
      subtitle="RBAC scope is enforced by backend result set, including tenant, permission, and organization boundaries."
    >
      {/* Search Header Bar */}
      <div className="cmts-header-toolbar">
        <div className="cmts-search-wrapper">
          <svg
            className="cmts-search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            className="cmts-search-input"
            aria-label="Search complaints"
            value={searchText}
            onChange={(event) => setSearchText(event.currentTarget.value)}
            onKeyDown={(event) => event.key === "Enter" && handleSearch()}
            placeholder="Search complaint code, keyword, or title..."
          />
          <Button label="Search" variant="secondary" onClick={handleSearch} />
        </div>

        <div className="cmts-toolbar-actions">
          <Button
            label="Create complaint"
            variant="primary"
            onClick={() => navigate("/cmts/complaints/new")}
          />
        </div>
      </div>

      {/* Error Alert */}
      {error ? (
        <div className="cmts-alert cmts-alert--error" role="alert">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      ) : null}

      {/* Main Data Grid with Export + Immediate File Download */}
      <div className="cmts-grid-wrapper">
        <CmtsSmartDataGrid<ComplaintGridRow>
          title="Complaint Records"
          subtitle={`${rows.length} records available`}
          rows={rows}
          columns={columns}
          rowKey="id"
          defaultPageSize={25}
          loading={loading}
          error={error ?? null}
          emptyMessage="No complaints match your current active filters."
          exportable={true}
          onExport={async (exportedRows) => {
            const dataToExport =
              exportedRows && exportedRows.length > 0 ? exportedRows : rows;

            // 1. Triggers automatic browser file download (CSV format)
            const timestamp = new Date().toISOString().split("T")[0];
            downloadDatasetFile(
              dataToExport,
              `complaint-register-${timestamp}.csv`,
            );

            // 2. Notifies parent component/prop callback if present
            if (props.onExport) {
              await props.onExport({ rows: dataToExport });
            }
          }}
          onRowClick={(row) => void props.onSelect?.(row)}
        />
      </div>

      {/* Enterprise Actions Drawer/Modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Complaint Quick Actions"
        subtitle={actionsFor ? `Reference: ${actionsFor.complaintCode}` : ""}
        size="md"
        onClose={() => setActionsFor(null)}
      >
        {actionsFor && (
          <div className="cmts-modal-container">
            {/* Meta Summary Cards */}
            <div className="cmts-summary-grid">
              <div className="cmts-summary-item">
                <span className="cmts-summary-item__label">Code</span>
                <strong className="cmts-summary-item__value">
                  {actionsFor.complaintCode}
                </strong>
              </div>
              <div className="cmts-summary-item">
                <span className="cmts-summary-item__label">Status</span>
                <StatusBadge status={actionsFor.status} />
              </div>
              <div className="cmts-summary-item">
                <span className="cmts-summary-item__label">Severity</span>
                <SeverityBadge severity={actionsFor.severity} />
              </div>
              <div className="cmts-summary-item">
                <span className="cmts-summary-item__label">SLA</span>
                <SlaBadge slaState={actionsFor.slaState} />
              </div>
            </div>

            {/* Action Cards */}
            <div className="cmts-actions-list">
              <ActionCard
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                }
                title="Open Details"
                description="View complete timeline, notes, and activity history."
                onClick={() => {
                  const row = actionsFor;
                  setActionsFor(null);
                  void props.onSelect?.(row);
                }}
              />

              <ActionCard
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                }
                title="Manage Workflow"
                description="Update progress, reassign, or update resolution state."
                onClick={() => {
                  const row = actionsFor;
                  setActionsFor(null);
                  void props.onOpenWorkflow?.(row);
                }}
              />

              <ActionCard
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                  </svg>
                }
                title="Evidence & Attachments"
                description="Upload or review supporting documents and images."
                onClick={() => {
                  const row = actionsFor;
                  setActionsFor(null);
                  void props.onOpenEvidence?.(row);
                }}
              />
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}
